import { createHash } from 'crypto';
import DOMPurify from 'dompurify';
import { LEAD_REGISTRATION_API, BRAND_NAME, COOKIE_NAMES } from '../constants';
import { getCookieValue, setCookie } from '../cookie';
import { convertStringToURL } from '../helpers';


const lpRegistrationFold = document.querySelector('.register-form');
const thankYouFold = document.querySelector('.thankyou-msg');

const ShortLpPagePaths = ['/lp/call-answering-services', '/lp/telephone-answering-services', '/lp/virtual-receptionist-services'];
const isFromShortLpPage = ShortLpPagePaths.includes(document.location.pathname) || document.location.pathname.includes('redeem-your-referral');

if (lpRegistrationFold && getCookieValue('isregistered')) {
  lpRegistrationFold.classList.add('hide');
  thankYouFold.classList.remove('hide');
}

export function updateAllSignupLinks(uniquePin) {
  try {
    if (!uniquePin) return;

    const signupLinkAnchorTags = Array.from(document.querySelectorAll('a'))
      .filter(tag => tag.href.includes('https://join.'));

    signupLinkAnchorTags.forEach((tag) => {
      const urlObj = convertStringToURL(tag.href);

      if (!urlObj) return;

      const { searchParams } = urlObj;
      if (searchParams.has('directSignup')) {
        searchParams.set('leadUniquePin', uniquePin);
        searchParams.delete('directSignup');
      } else if (searchParams.has('leadUniquePin')) {
        searchParams.set('leadUniquePin', uniquePin);
      }

      tag.setAttribute('href', urlObj.toString());
    });
  } catch (e) {
    console.error('Error updating signup link:', e);
  }
}


export function showPlansFold(uniquePin) {
  const plansFold = document.querySelector('#plans-wrapper');
  const leadRegistrationFold = document.querySelector('#registration');
  if (plansFold) {
    if (uniquePin) updateAllSignupLinks(uniquePin);
    plansFold.classList.remove('hide');
  }
  if (leadRegistrationFold && !isFromShortLpPage) leadRegistrationFold.classList.add('hide');
}

export function onFocus(event) {
  if (event.target.classList.contains('invalid')) {
    event.target.classList.remove('invalid');
  }
}

export function isLeadInfoValid(leadInfo) {
  return (
    leadInfo && Object.keys(leadInfo).length
    && Object.values(leadInfo).every(value => value.valid)
  );
}

function hash(string) {
  return createHash('sha256').update(string).digest('hex');
}

export function pushRegistrationToGA4DataLayer(brandName, response) {
  if (window.dataLayer) {
    window.dataLayer.push({
      brand: brandName,
      source: 'internal',
      event: 'purchase',
      ecommerce: {
        transaction_id: response.UniquePIN,
        affiliation: 'Leads',
        value: 0.0,
        tax: 0.0,
        shipping: 0.0,
        currency: '',
        coupon: '',
        items: [
          {
            item_name: 'Leads',
            item_id: response.UniquePIN,
            Price: 0.0,
            item_brand: '',
            item_category: 'Website Leads',
            item_category_2: '',
            item_category_3: '',
            item_category_4: '',
            item_category_5: '',
            item_variant: '',
            quantity: 1,
          },
        ],
      },
      contact_name: hash(response.fullName),
      contact_email: hash(response.email),
      contact_phone: hash(response.phone),
    });
  }
}

export function setPhoneFlagAttributes() {
  const flagContainer = document.querySelector('.iti__flag-container');
  const selectedFlag = document.querySelector('.iti__selected-flag');
  if (flagContainer) {
    flagContainer.setAttribute('aria-label', 'Country code');
  }

  if (selectedFlag) {
    selectedFlag.setAttribute('role', 'button');
    selectedFlag.setAttribute('aria-haspopup', 'true');
  }
}

export function highlightInvalidFields(leadInfo = {}) {
  Object.values(leadInfo).forEach((value) => {
    if (!value.valid && value.selector) {
      document.querySelector(value.selector)?.classList.add('invalid');
    }
  });
}

export function getRefinedLeadInfo(leadInfo = {}) {
  return Object.values(leadInfo).reduce((prevValue, currentValue) => {
    const prevValueRef = prevValue;
    const { value, emittedBy } = currentValue;
    if (emittedBy === 'phone') {
      const { dialCode, nationalNumber } = currentValue;
      prevValueRef.countryCode = dialCode;
      prevValueRef.phone = nationalNumber;
    } else {
      prevValueRef[emittedBy] = value;
    }
    return prevValueRef;
  }, {});
}

export function pushRegInfoToGTM(response) {
  window.localStorage.setItem(
    'gtmData',
    JSON.stringify([
      {
        source: response.source,
        medium: response.medium,
        campaign: response.campaign,
        term: response.term,
        gclid: response.gclid,
        event: 'Registration',
        transactionId: response.UniquePIN,
        transactionAffiliation: 'Registration',
        transactionTotal: 0,
        transactionShipping: 0,
        transactionTax: 0,
        transactionCurrency: 'USD',
        transactionProducts: [
          {
            sku: 'Lead',
            category: '',
            name: 'Lead',
            price: 0,
            quantity: 1,
          },
        ],
      },
    ]),
  );

  pushRegistrationToGA4DataLayer(BRAND_NAME, response);
}

export async function processLeadRegistration(payload, successCallback, failureCallback) {
  try {
    let response = await window.fetch(LEAD_REGISTRATION_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    response = await response.json();
    if (response && Object.keys(response).length) {
      const { UniquePIN, signupurl } = response;
      if (UniquePIN && signupurl) {
        pushRegInfoToGTM(response);
        setCookie('isregistered', true, 365);
        setCookie('uniquePin', UniquePIN, 365);
        if (window.location.pathname !== '/view-pricing' && !lpRegistrationFold) {
          showPlansFold(UniquePIN);
        }
        if (lpRegistrationFold) {
          lpRegistrationFold.classList.add('hide');
          thankYouFold.classList.remove('hide');
          document.getElementById('registration').classList.remove('loading');
        }
        successCallback(response);
      } else {
        failureCallback();
      }
    } else {
      failureCallback();
    }
  } catch (e) {
    console.error(e); // eslint-disable-line
    failureCallback();
  }
}

export function getSanitizedInput(userInput = '') {
  return DOMPurify.sanitize(userInput, {
    ALLOWED_TAGS: '',
    ALLOWED_ATTR: '',
  });
}


export const isRegistered = () => getCookieValue(COOKIE_NAMES.IS_REGISTERED) === 'true';
export const isTestRegistered = () => getCookieValue(COOKIE_NAMES.IS_TEST_REGISTERED) === 'true';
export const isDemoRegistered = () => getCookieValue(COOKIE_NAMES.IS_DEMO_REGISTERED) === 'true';
